import { ChevronRight } from "lucide-react";
import { getIconComponent, type IconType } from "@/lib/icon-registry";
import { cn } from "@/lib/utils";

type ActivitySkill = {
  activityId: string;
  activity: {
    id: string;
    name: string;
    metric: string;
    iconKey: string;
  } | null;
  level: {
    score: number;
    label: string;
    sublabel?: string;
  } | null;
};

const getSkillStyles = (levelId: number | undefined) => {
  const id = levelId || 0;
  // 7-level system: 1-Initiation, 2-Beginner, 3-Beginner+, 4-Intermediate, 5-Intermediate+, 6-Advanced, 7-Pro
  switch (id) {
    case 7: // Pro
      return {
        icon: "text-orange-600",
        badge: "bg-orange-100 text-orange-700 border-orange-200",
      };
    case 6: // Advanced
      return {
        icon: "text-rose-600",
        badge: "bg-rose-100 text-rose-700 border-rose-200",
      };
    case 5: // Intermediate+
      return {
        icon: "text-purple-600",
        badge: "bg-purple-100 text-purple-700 border-purple-200",
      };
    case 4: // Intermediate
      return {
        icon: "text-blue-600",
        badge: "bg-blue-100 text-blue-700 border-blue-200",
      };
    case 3: // Beginner+
      return {
        icon: "text-teal-600",
        badge: "bg-teal-100 text-teal-700 border-teal-200",
      };
    case 2: // Beginner
      return {
        icon: "text-emerald-600",
        badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
      };
    default: // Initiation (1) or unset
      return {
        icon: "text-slate-600",
        badge: "bg-slate-100 text-slate-700 border-slate-200",
      };
  }
};

type ActivityCardProps = {
  activitySkill: ActivitySkill;
  isSelected?: boolean;
  onSelect?: () => void;
  showSelectionIndicator?: boolean;
  variant?: "default" | "compact";
  className?: string;
};

export function ActivityCard({
  activitySkill,
  isSelected = false,
  onSelect,
  showSelectionIndicator = false,
  variant = "default",
  className,
}: ActivityCardProps) {
  const { activity, level } = activitySkill;

  if (!activity) {
    return null;
  }

  const Icon = getIconComponent(activity.iconKey as IconType);
  const styles = getSkillStyles(level?.score);

  const cardContent = (
    <>
      <div
        className={cn(
          "flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-xl bg-surface-alt transition-colors",
        )}
      >
        <Icon className={cn("h-10 w-10 transition-colors", styles.icon)} />
      </div>
      <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 py-0.5">
          <span
            className={cn(
              "leading-snug font-semibold tracking-tight text-text-primary",
              variant === "compact" ? "text-sm" : "text-base",
            )}
          >
            {activity.name}
          </span>
          <div className="flex flex-col">
            {level && (
              <span
                className={cn(
                  "leading-tight font-medium text-text-primary",
                  variant === "compact" ? "text-xs" : "text-sm",
                )}
              >
                {level.label}
              </span>
            )}
            <span
              className={cn(
                "truncate leading-tight font-medium opacity-80",
                variant === "compact"
                  ? "text-[10px] text-text-secondary"
                  : "text-xs text-text-secondary",
              )}
            >
              {level?.sublabel || activity.metric}
            </span>
          </div>
        </div>

        {level && (
          <div className="flex shrink-0 items-start pt-[3px]">
            <span
              className={cn(
                "font-bold tracking-wider text-text-tertiary uppercase",
                variant === "compact" ? "text-[10px]" : "text-[11px]",
              )}
            >
              Level {level.score}
            </span>
          </div>
        )}
      </div>
      {showSelectionIndicator && (
        <div
          className={cn(
            "flex items-center",
            isSelected ? "text-brand" : "text-text-tertiary",
          )}
        >
          <ChevronRight className="h-5 w-5" />
        </div>
      )}
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-3 rounded-2xl border p-2 text-left transition-all active:scale-[0.99]",
          isSelected
            ? "border-brand bg-brand/5 ring-1 ring-brand/20"
            : "border-border bg-surface hover:bg-surface-alt/50",
          variant === "compact" && "gap-3 p-2",
          className,
        )}
        onClick={onSelect}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-2xl border border-border bg-surface p-2 transition-all",
        variant === "compact" && "gap-3 p-2",
        className,
      )}
    >
      {cardContent}
    </div>
  );
}
